import type { Translate } from 'next-translate';

import { formatDollars } from '~/domains/common/utils/formatters';
import type {
  NormalizedBonus,
  PromoCodeResponse,
  BonusDefinitionResponse,
  VoucherDefinitionResponse,
  PublicBoost,
} from '~/domains/promotions/types';
import { BoostCategory, PromoType } from '~/domains/promotions/types';

/**
 * Rewards bundle (boosts / vouchers / bonuses) shared by the promo-code and
 * mission-detail responses. Maps to the PromoTicket carousel cards.
 */
type Rewards = {
  boosts?: PublicBoost[];
  vouchers?: VoucherDefinitionResponse[];
  bonuses?: BonusDefinitionResponse[];
};

/**
 * Maps a rewards bundle into PromoTicket cards. Consumed by both the promo-code
 * bonus modal and the mission COMPLETED modal (rewards are the same schema).
 */
export const parseRewardsToPromoTickets = (
  { boosts = [], vouchers = [], bonuses = [] }: Rewards,
  t: Translate<'promotions'>,
) => [
    ...boosts.map((boost) => ({
      promotionType: PromoType.BOOST,
      id: boost.id,
      // eslint-disable-next-line no-nested-ternary
      amount: boost.type === BoostCategory.RISK_FREE
        ? formatDollars(boost.max_bet_option)
        : boost.payout_type === 'fixed'
          ? formatDollars(boost.amount)
          : `${boost.amount * 100}%`,
      title1: t('bonusModal.promoTicket.qpBoost.title1'),
      title2: boost.type === BoostCategory.PAYOUT_BOOST
        ? t('bonusModal.promoTicket.qpBoost.title2boost')
        : t('bonusModal.promoTicket.qpBoost.title2noSweat'),
      description1: boost.description,
    })),
    ...vouchers.map((voucher) => ({
      promotionType: PromoType.VOUCHER,
      id: voucher.id,
      amount: formatDollars(voucher.maxValue / 100),
      title1: t('bonusModal.promoTicket.voucher.title1'),
      title2: t('bonusModal.promoTicket.voucher.title2'),
      description1: voucher.description ?? t(
        'bonusModal.promoTicket.voucher.description1',
        { league: voucher.league ?? 'any', contestType: voucher.contestType?.replace('_', ' ') ?? '' },
      ),
      description2: voucher.description ? undefined : t(
        'bonusModal.promoTicket.voucher.description2',
        { maxValue: formatDollars(voucher.maxValue / 100) }
      ),
    })),
    ...bonuses.map((bonus) => ({
      promotionType: PromoType.DEPOSIT_BONUS,
      id: bonus.id,
      // eslint-disable-next-line no-nested-ternary
      amount: bonus.calculationType === 'PERCENTAGE'
        ? `${bonus.percentage}%`
        : bonus.flatAmountCents
          ? formatDollars(bonus.flatAmountCents / 100)
          : undefined
      ,
      title1: bonus.typeName ?? t('bonusModal.promoTicket.deposit.title1'),
      title2: !bonus.typeName ? t('bonusModal.promoTicket.deposit.title2') : undefined,
      description1: bonus.subtitle || bonus.description,
    })),
  ];

export const parsePromoTicketsFromPromoCode = (
  promoCodeDetail: PromoCodeResponse,
  t: Translate<'promotions'>,
) => {
  const { creditAmount } = promoCodeDetail;

  return [
    ...(creditAmount ? [{
      promotionType: PromoType.CREDIT,
      id: promoCodeDetail.id,
      amount: formatDollars(creditAmount),
      title1: t('bonusModal.promoTicket.credit.title1'),
      title2: t('bonusModal.promoTicket.credit.title2'),
      description1: t('bonusModal.promoTicket.credit.description1'),
    }] : []),
    ...parseRewardsToPromoTickets(promoCodeDetail, t),
  ];
};

export const parsePromoTicketFromReferralCode = (
  referralCode: string,
  t: Translate<'promotions'>,
) => [{
    promotionType: PromoType.REFERRAL,
    id: referralCode,
    amount: formatDollars(10),
    title1: t('bonusModal.promoTicket.referral.title1'),
    title2: t('bonusModal.promoTicket.referral.title2'),
    description1: t('bonusModal.promoTicket.referral.description1', { amount: formatDollars(10) }),
  }];

export const parsePromoTicketFromDepositBonus = (
  bonus: NormalizedBonus,
  t: Translate<'promotions'>
) => [
  {
    promotionType: PromoType.DEPOSIT_BONUS,
    id: bonus.id,
    amount: bonus.bonusAmountDisplay,
    title1: bonus.isPercentage
      ? t('bonusModal.promoTicket.depositMatch.title1')
      : t('bonusModal.promoTicket.deposit.title1'),
    title2: bonus.isPercentage
      ? t('bonusModal.promoTicket.depositMatch.title2')
      : t('bonusModal.promoTicket.deposit.title2'),
    description1: bonus.subtitle || bonus.description,
  },
];
